import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, Button, Select } from "antd";
import { ReloadOutlined, ArrowRightOutlined } from "@ant-design/icons";
import {
  TbCurrencyDollar,
  TbShoppingCart,
  TbReceipt,
  TbAlertTriangle,
  TbCreditCard,
  TbBuildingStore,
  TbTrendingUp,
  TbDiscount,
  TbClock,
  TbAlertCircle,
} from "react-icons/tb";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { useSaleSummary, GetSummaryParamsI, getAllBranchsByToken } from "@apis";
import { BranchStkI, SaleSummaryI } from "@interfaces";
import { useRouterStore } from "@stores";
import { SubscriptionStatusEnum } from "@enums";
import "../../css/operator.css";

type DashPeriod = "today" | "week";

const COLORS = ["#1e1948", "#1D9E75", "#534AB7", "#854F0B", "#0C447C"];

const PERIOD_OPTIONS: { value: DashPeriod; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "week", label: "Esta semana" },
];

// ── skeleton ──────────────────────────────────────────────
const Skeleton = ({ w = "100%", h = 20 }: { w?: string; h?: number }) => (
  <div className="do-skeleton" style={{ width: w, height: h }} />
);

// ── KPI card ─────────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <div className="do-kpi-card">
    <div className="do-kpi-icon" style={{ background: `${color}18`, color }}>
      {icon}
    </div>
    <div className="do-kpi-info">
      <span className="do-kpi-value">{value}</span>
      <span className="do-kpi-label">{label}</span>
      {sub && <span className="do-kpi-sub">{sub}</span>}
    </div>
  </div>
);

const DashboardOperatorComponent = () => {
  const navigate = useNavigate();
  const subscriptionStatusCode = useRouterStore(
    (s) => s.subscriptionStatusCode,
  );
  const daysLeft = useRouterStore((s) => s.daysLeft);
  const [branches, setBranches] = React.useState<BranchStkI[]>([]);
  const [branchId, setBranchId] = React.useState<string | undefined>();
  const [branchName, setBranchName] = React.useState<string>("");
  const [loadingBranches, setLoadingBranches] = React.useState(true);
  const [period, setPeriod] = useState<DashPeriod>("today");

  // ── cargar sucursales del operador ────────────────────────
  React.useEffect(() => {
    getAllBranchsByToken()
      .then((res) => {
        const data = res.data ?? [];
        setBranches(data);
        // si tiene al menos una, la selecciona automáticamente
        if (data.length > 0) {
          setBranchId(data[0].id);
          setBranchName(data[0].name);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingBranches(false));
  }, []);

  const summaryParams = useMemo<GetSummaryParamsI>(
    () => ({ period, branchId }),
    [period, branchId],
  );

  const { summaryQuery } = useSaleSummary(summaryParams);
  const {
    data: summaryData,
    isLoading: loadingSummary,
    refetch,
    isFetching,
  } = summaryQuery;

  const summary: SaleSummaryI | undefined = summaryData?.data;
  const isLoading = loadingSummary || isFetching || loadingBranches;

  const fmt = (n?: number) =>
    `$${(n ?? 0).toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtNum = (n?: number) => (n ?? 0).toLocaleString("es-EC");

  const handleBranchChange = (id: string) => {
    const found = branches.find((b) => b.id === id);
    setBranchId(id);
    setBranchName(found?.name ?? "");
  };

  return (
    <div className="do-page">
      {subscriptionStatusCode === SubscriptionStatusEnum.ABOUT_TO_EXPIRE && (
        <div
          className={`do-sub-banner ${daysLeft !== null && daysLeft <= 2 ? "danger" : "warning"}`}
        >
          <div className="do-sub-banner-icon">
            {daysLeft !== null && daysLeft <= 2 ? (
              <TbAlertCircle size={18} />
            ) : (
              <TbClock size={18} />
            )}
          </div>
          <div className="do-sub-banner-text">
            {daysLeft === 0 ? (
              <>
                <strong>Tu suscripción vence hoy.</strong> Contacta a tu
                administrador para renovar y evitar perder el acceso.
              </>
            ) : daysLeft === 1 ? (
              <>
                <strong>Tu suscripción vence mañana.</strong> Contacta a tu
                administrador para renovar.
              </>
            ) : (
              <>
                <strong>Tu suscripción vence en {daysLeft} días.</strong>{" "}
                Comunícate con tu administrador para renovar a tiempo.
              </>
            )}
          </div>
          <div className="do-sub-banner-days">
            {daysLeft !== null && daysLeft >= 0 && (
              <span
                className={`do-sub-days-badge ${daysLeft <= 2 ? "danger" : "warning"}`}
              >
                {daysLeft === 0 ? "Vence hoy" : `${daysLeft}d restantes`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── topbar ── */}
      <div className="do-topbar">
        <div className="do-topbar-left">
          <div className="do-branch-badge">
            <TbBuildingStore size={14} />
            <span>
              {loadingBranches ? "Cargando..." : branchName || "Sin sucursal"}
            </span>
          </div>
          <h2 className="do-title">Mi sucursal</h2>
          <p className="do-subtitle">
            {dayjs().format("dddd, DD [de] MMMM YYYY")}
          </p>
        </div>

        <div className="do-topbar-right">
          {/* período */}
          <div className="do-period-tabs">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`do-period-btn ${period === p.value ? "active" : ""}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* selector sucursal — solo si tiene más de una */}
          {branches.length > 1 && (
            <Select
              size="small"
              className="do-select"
              value={branchId}
              onChange={handleBranchChange}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
            />
          )}

          <Tooltip title="Refrescar">
            <Button
              shape="circle"
              icon={<ReloadOutlined />}
              size="small"
              loading={isLoading}
              onClick={() => refetch()}
              className="do-refresh"
            />
          </Tooltip>

          <Button
            size="small"
            type="dashed"
            icon={<ArrowRightOutlined />}
            className="do-btn-advanced"
            onClick={() => navigate("/stockify/history/show")}
          >
            Ver reportes
          </Button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="do-kpi-grid">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="do-kpi-card do-kpi-skeleton">
              <Skeleton w="38px" h={38} />
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <Skeleton w="60%" h={20} />
                <Skeleton w="40%" h={13} />
              </div>
            </div>
          ))
        ) : (
          <>
            <KpiCard
              icon={<TbCurrencyDollar size={20} />}
              label="Total vendido"
              value={fmt(summary?.totals.totalSales)}
              sub={`${fmtNum(summary?.totals.countSales)} ventas`}
              color="#1D9E75"
            />
            <KpiCard
              icon={<TbShoppingCart size={20} />}
              label="Ticket promedio"
              value={fmt(summary?.totals.averageTicket)}
              color="#534AB7"
            />
            <KpiCard
              icon={<TbReceipt size={20} />}
              label="IVA cobrado"
              value={fmt(summary?.totals.totalIva)}
              color="#0C447C"
            />
            <KpiCard
              icon={<TbDiscount size={20} />}
              label="Descuentos"
              value={fmt(summary?.totals.totalDiscount)}
              color="#854F0B"
            />
            <KpiCard
              icon={<TbCreditCard size={20} />}
              label="CxC pendiente"
              value={fmt(summary?.receivables.totalPending)}
              sub={`${fmtNum(summary?.receivables.count)} cuentas`}
              color="#A32D2D"
            />
          </>
        )}
      </div>

      {/* ── fila principal ── */}
      <div className="do-main-row">
        {/* gráfica ventas por día */}
        <div className="do-chart-card do-chart-main">
          <div className="do-chart-header">
            <span className="do-chart-title">
              <TbTrendingUp size={14} />
              {period === "today" ? "Ventas de hoy" : "Ventas de la semana"}
            </span>
            <span className="do-chart-total">
              {isLoading ? "—" : fmt(summary?.totals.totalSales)}
            </span>
          </div>
          {isLoading ? (
            <Skeleton w="100%" h={180} />
          ) : (summary?.byDay ?? []).length === 0 ? (
            <div className="do-chart-empty">Sin ventas en el período</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart
                data={summary?.byDay ?? []}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gradOp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1D9E75" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1D9E75" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#888780" }}
                  tickFormatter={(v) =>
                    period === "today"
                      ? dayjs(v).format("HH:mm")
                      : dayjs(v).format("DD/MM")
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#888780" }}
                  tickFormatter={(v) => `$${v}`}
                  width={55}
                />
                <RTooltip
                  formatter={(v: number) => [fmt(v), "Total"]}
                  labelFormatter={(l) => dayjs(l).format("DD MMM YYYY")}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e0dff5",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#1D9E75"
                  strokeWidth={2}
                  fill="url(#gradOp)"
                  dot={{ r: 3, fill: "#1D9E75" }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* donut método de pago */}
        <div className="do-chart-card">
          <div className="do-chart-header">
            <span className="do-chart-title">Método de pago</span>
          </div>
          {isLoading ? (
            <div className="do-chart-empty">
              <Skeleton w="160px" h={160} />
            </div>
          ) : (summary?.byPaymentMethod ?? []).length === 0 ? (
            <div className="do-chart-empty">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={summary?.byPaymentMethod ?? []}
                  dataKey="total"
                  nameKey="method"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={72}
                  paddingAngle={3}
                >
                  {(summary?.byPaymentMethod ?? []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v: number) => [fmt(v), "Total"]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e0dff5",
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── fila inferior ── */}
      <div className="do-bottom-row">
        {/* top productos */}
        <div className="do-chart-card">
          <div className="do-chart-header">
            <span className="do-chart-title">Top 5 productos</span>
          </div>
          {isLoading ? (
            <div className="do-top-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} w="100%" h={36} />
              ))}
            </div>
          ) : (summary?.topProducts ?? []).length === 0 ? (
            <div className="do-chart-empty">Sin ventas en el período</div>
          ) : (
            <div className="do-top-list">
              {(summary?.topProducts ?? []).map((p, i) => {
                const maxQty = Math.max(
                  ...(summary?.topProducts ?? [{ quantity: 1 }]).map(
                    (x) => x.quantity,
                  ),
                );
                const pct = Math.round((p.quantity / maxQty) * 100);
                return (
                  <div key={i} className="do-top-item">
                    <span className="do-top-rank">{i + 1}</span>
                    <div className="do-top-info">
                      <span className="do-top-name">{p.product}</span>
                      <div className="do-top-bar-bg">
                        <div
                          className="do-top-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="do-top-nums">
                      <span className="do-top-qty">
                        {fmtNum(p.quantity)} und
                      </span>
                      <span className="do-top-total">{fmt(p.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* alertas */}
        <div className="do-chart-card do-alerts-card">
          <div className="do-chart-header">
            <span className="do-chart-title">
              <TbAlertTriangle size={14} /> Estado de mi sucursal
            </span>
          </div>

          <div className="do-alerts-list">
            <div
              className={`do-alert-item ${(summary?.lowStockCount ?? 0) > 0 ? "warn" : "ok"}`}
            >
              <div className="do-alert-icon">
                <TbAlertTriangle size={15} />
              </div>
              <div className="do-alert-info">
                <span className="do-alert-label">Productos con stock bajo</span>
                <span className="do-alert-value">
                  {isLoading ? "—" : `${summary?.lowStockCount ?? 0} productos`}
                </span>
              </div>
              {(summary?.lowStockCount ?? 0) > 0 && (
                <span className="do-alert-badge warn">
                  {summary?.lowStockCount}
                </span>
              )}
            </div>

            <div
              className={`do-alert-item ${(summary?.receivables.count ?? 0) > 0 ? "danger" : "ok"}`}
            >
              <div className="do-alert-icon">
                <TbCreditCard size={15} />
              </div>
              <div className="do-alert-info">
                <span className="do-alert-label">CxC pendientes</span>
                <span className="do-alert-value">
                  {isLoading ? "—" : fmt(summary?.receivables.totalPending)}
                </span>
              </div>
              {(summary?.receivables.count ?? 0) > 0 && (
                <span className="do-alert-badge danger">
                  {summary?.receivables.count}
                </span>
              )}
            </div>

            <div className="do-alert-item neutral">
              <div className="do-alert-icon">
                <TbShoppingCart size={15} />
              </div>
              <div className="do-alert-info">
                <span className="do-alert-label">Ventas registradas hoy</span>
                <span className="do-alert-value">
                  {isLoading
                    ? "—"
                    : `${fmtNum(summary?.totals.countSales)} ventas`}
                </span>
              </div>
            </div>

            <div className="do-alert-item neutral">
              <div className="do-alert-icon">
                <TbDiscount size={15} />
              </div>
              <div className="do-alert-info">
                <span className="do-alert-label">Descuentos aplicados</span>
                <span className="do-alert-value">
                  {isLoading ? "—" : fmt(summary?.totals.totalDiscount)}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="do-btn-goto"
            onClick={() => navigate("/stockify/history/show")}
          >
            Ver reportes completos <ArrowRightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardOperatorComponent;
